/**
 * Redaction Panel Component
 * Sensitive data detection and redaction suggestions
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Shield, AlertTriangle, Eye, EyeOff, 
  RefreshCw, FileWarning, CheckCircle, XCircle,
  CreditCard, Phone, Mail, User
} from 'lucide-react';
import agenticApi from '../../api/agenticApi';
import { toast } from 'sonner';

const RedactionPanel = ({ headers, docId = null, initialText = '' }) => {
  const [text, setText] = useState(initialText);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const analyzeText = async () => {
    if (!text.trim()) {
      toast.error('Please enter text to analyze');
      return;
    }

    setLoading(true);
    try {
      const response = await agenticApi.detectSensitiveData(text, null, 'low', headers);
      setResult(response.data);
      
      if (response.data.total_detections > 0) {
        toast.warning(`Found ${response.data.total_detections} sensitive items`);
      } else {
        toast.success('No sensitive data detected');
      }
    } catch (err) {
      console.error('Detection failed:', err);
      toast.error(err.response?.data?.detail || 'Detection failed');
    } finally {
      setLoading(false);
    }
  };

  const analyzeDocument = async () => {
    if (!docId) return;
    
    setLoading(true);
    try {
      const response = await agenticApi.getDocumentRedactionSuggestions(docId, headers);
      setResult(response.data);
      setText(response.data.text_analyzed || '');
      
      if (response.data.total_detections > 0) {
        toast.warning(`Found ${response.data.total_detections} sensitive items`);
      } else {
        toast.success('No sensitive data detected');
      }
    } catch (err) {
      console.error('Document analysis failed:', err);
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[severity] || colors.low;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'identity':
        return <User className="w-4 h-4" />;
      case 'financial':
        return <CreditCard className="w-4 h-4" />;
      case 'contact':
        return <Phone className="w-4 h-4" />;
      default:
        return <FileWarning className="w-4 h-4" />;
    }
  };

  const highlightSensitiveData = () => {
    if (!result || !result.detections || result.detections.length === 0) {
      return text;
    }

    let highlightedText = text;
    const sortedDetections = [...result.detections].sort(
      (a, b) => b.position.start - a.position.start
    );

    sortedDetections.forEach(det => {
      const before = highlightedText.slice(0, det.position.start);
      const match = highlightedText.slice(det.position.start, det.position.end);
      const after = highlightedText.slice(det.position.end);
      highlightedText = `${before}<mark class="bg-red-200 px-1 rounded">${showOriginal ? match : det.masked_value}</mark>${after}`;
    });

    return highlightedText;
  };

  return (
    <div className="space-y-4" data-testid="redaction-panel">
      {/* Input Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Sensitive Data Detection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">
              Enter or paste text to analyze for sensitive data
            </label>
            <textarea
              className="w-full p-3 border rounded-lg h-32 resize-none text-sm"
              placeholder="Paste document text here... (e.g., PAN: ABCDE1234F, Phone: +91 9876543210)"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="redaction-text-input"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={analyzeText}
              disabled={loading || !text.trim()}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="analyze-text-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Detect Sensitive Data
                </>
              )}
            </Button>
            
            {docId && (
              <Button
                variant="outline"
                onClick={analyzeDocument}
                disabled={loading}
              >
                Analyze Document
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className={result.summary?.risk_level === 'critical' ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="pt-4">
                <div className="text-xs text-slate-500 mb-1">Risk Level</div>
                <Badge className={getSeverityColor(result.summary?.risk_level)}>
                  {result.summary?.risk_level?.toUpperCase() || 'NONE'}
                </Badge>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-slate-500 mb-1">Total Detections</div>
                <div className="text-2xl font-bold text-slate-800">
                  {result.total_detections || 0}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-slate-500 mb-1">High Severity</div>
                <div className="text-2xl font-bold text-orange-600">
                  {(result.summary?.by_severity?.high || 0) + (result.summary?.by_severity?.critical || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-slate-500 mb-1">Categories</div>
                <div className="text-2xl font-bold text-slate-800">
                  {Object.keys(result.summary?.by_category || {}).length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Highlighted Preview */}
          {result.detections?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  Preview with Detections
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setShowOriginal(!showOriginal)}
                  >
                    {showOriginal ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide Original
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Show Original
                      </>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: highlightSensitiveData() }}
                />
              </CardContent>
            </Card>
          )}

          {/* Detections List */}
          {result.detections?.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Detected Sensitive Data
                  <Badge className="bg-amber-100 text-amber-800 ml-auto">
                    {result.total_detections}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {result.detections.map((det, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border ${getSeverityColor(det.severity)}`}
                    >
                      <div className="flex items-start gap-3">
                        {getCategoryIcon(det.category)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{det.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {det.severity}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-600 mt-1 font-mono">
                            {showOriginal ? det.value : det.masked_value}
                          </div>
                          {det.context && (
                            <div className="text-xs text-slate-500 mt-1 truncate">
                              Context: {det.context}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : result.total_detections === 0 && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-6 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <h3 className="font-medium text-green-800">No Sensitive Data Detected</h3>
                <p className="text-sm text-green-700 mt-1">
                  The analyzed text appears to be free of PII and sensitive information.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Redaction Suggestions */}
          {result.redaction_suggestions?.length > 0 && (
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  Redaction Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div className="text-xs text-amber-800">
                      <strong>Confirmation Required:</strong> These are suggestions only. 
                      Review each item carefully before applying redactions.
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {result.redaction_suggestions.slice(0, 10).map((suggestion, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                      <div className="flex-1 text-sm">
                        <span className="font-mono text-red-600 line-through mr-2">
                          {showOriginal ? suggestion.original : '***'}
                        </span>
                        <span className="text-slate-400">→</span>
                        <span className="font-mono text-green-600 ml-2">
                          {suggestion.replacement}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {suggestion.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default RedactionPanel;
